import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import { Dialog, IconButton, Typography } from '@mui/material'
import { useCallback, useEffect, useState, type FC, type MouseEvent } from 'react'
import WoodworkPhoto from '../components/WoodworkPhoto'
import type { WoodworkGalleryImage } from '../api-hooks/useWoodworkProjects'
import './WorkProject.css'

type WorkProjectGalleryProps = {
  readonly images: readonly WoodworkGalleryImage[]
  readonly projectTitle: string
}

const WorkProjectGallery: FC<WorkProjectGalleryProps> = ({ images, projectTitle }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const closeLightbox = useCallback(() => setLightboxIndex(null), [])

  const showPrev = useCallback(() => {
    setLightboxIndex((index) => {
      if (index === null || images.length === 0) return index
      return (index - 1 + images.length) % images.length
    })
  }, [images.length])

  const showNext = useCallback(() => {
    setLightboxIndex((index) => {
      if (index === null || images.length === 0) return index
      return (index + 1) % images.length
    })
  }, [images.length])

  useEffect(() => {
    if (lightboxIndex === null) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox()
      if (event.key === 'ArrowLeft') showPrev()
      if (event.key === 'ArrowRight') showNext()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [lightboxIndex, closeLightbox, showPrev, showNext])

  const stopPropagation = (event: MouseEvent) => event.stopPropagation()

  if (images.length === 0) {
    return (
      <div className='placeholder-media'>
        <span className='placeholder-media__label'>No photos yet</span>
      </div>
    )
  }

  const active = lightboxIndex !== null ? images[lightboxIndex] : null
  const hasMultiple = images.length > 1
  const positionLabel =
    lightboxIndex !== null ? `${lightboxIndex + 1} / ${images.length}` : ''

  return (
    <>
      <div className='work-project-gallery'>
        {images.map((image, index) => {
          const isFeature = index === 0
          const caption = image.caption ?? image.alt
          return (
            <figure
              key={image.src}
              className={
                isFeature
                  ? 'work-project-gallery__item work-project-gallery__item--feature'
                  : 'work-project-gallery__item'
              }
            >
              <button
                type='button'
                className='work-project-gallery__button'
                onClick={() => setLightboxIndex(index)}
                aria-label={`View ${image.alt}`}
              >
                <WoodworkPhoto
                  layout={isFeature ? 'gallery-feature' : 'gallery'}
                  src={image.src}
                  alt={image.alt}
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
                {caption ? (
                  <span className='work-project-gallery__caption'>{caption}</span>
                ) : null}
              </button>
            </figure>
          )
        })}
      </div>

      <Dialog
        fullScreen
        open={lightboxIndex !== null}
        onClose={closeLightbox}
        className='work-project-lightbox'
        aria-labelledby='work-project-lightbox-title'
        PaperProps={{ className: 'work-project-lightbox__paper' }}
      >
        <div className='work-project-lightbox__stage' onClick={closeLightbox} role='presentation'>
          <Typography
            id='work-project-lightbox-title'
            component='h2'
            className='work-project-lightbox__sr-only'
          >
            {projectTitle}
          </Typography>

          {hasMultiple ? (
            <>
              <button
                type='button'
                className='work-project-lightbox__side work-project-lightbox__side--prev'
                onClick={(event) => {
                  stopPropagation(event)
                  showPrev()
                }}
                aria-label='Previous photo'
              >
                <ChevronLeftIcon fontSize='inherit' />
              </button>
              <button
                type='button'
                className='work-project-lightbox__side work-project-lightbox__side--next'
                onClick={(event) => {
                  stopPropagation(event)
                  showNext()
                }}
                aria-label='Next photo'
              >
                <ChevronRightIcon fontSize='inherit' />
              </button>
            </>
          ) : null}

          {active ? (
            <figure
              className='work-project-lightbox__figure'
              onClick={stopPropagation}
            >
              <WoodworkPhoto
                layout='lightbox'
                watermarkSize='lightbox'
                src={active.src}
                alt={active.alt}
              />
            </figure>
          ) : null}
        </div>

        <footer
          className='work-project-lightbox__bar'
          onClick={stopPropagation}
        >
          {hasMultiple ? (
            <IconButton
              className='work-project-lightbox__bar-btn'
              aria-label='Previous photo'
              onClick={showPrev}
              size='large'
            >
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <span className='work-project-lightbox__bar-spacer' aria-hidden />
          )}

          <div className='work-project-lightbox__bar-center'>
            {hasMultiple ? (
              <span className='work-project-lightbox__counter'>{positionLabel}</span>
            ) : null}
            {active && (active.caption ?? active.alt) ? (
              <Typography
                component='p'
                className='work-project-lightbox__caption'
              >
                {active.caption ?? active.alt}
              </Typography>
            ) : null}
          </div>

          {hasMultiple ? (
            <IconButton
              className='work-project-lightbox__bar-btn'
              aria-label='Next photo'
              onClick={showNext}
              size='large'
            >
              <ChevronRightIcon />
            </IconButton>
          ) : (
            <span className='work-project-lightbox__bar-spacer' aria-hidden />
          )}

          <IconButton
            className='work-project-lightbox__bar-btn work-project-lightbox__bar-close'
            aria-label='Close gallery'
            onClick={closeLightbox}
            size='large'
          >
            <CloseIcon />
          </IconButton>
        </footer>
      </Dialog>
    </>
  )
}

export default WorkProjectGallery
